// InboxPage — 受信箱ホーム「今日やること」 (#inbox, blueprint v2 裁定11, Phase 3 — 2026-07-05)
//
// 朝一でこの1画面を開けば当日のルーチン・対応待ち・バッチが全て判る日次ダッシュボード。
// 状態語彙は lib/statusVocab.ts (統一語彙) 準拠。
//   ① 今日のバッチ       — open バッチの「登録 n/N・照合 n/N」2段カウンタ (クリックで #batches/:id)
//   ② 対応待ち(承認)     — awaiting_approval ジョブのみ (インライン承認)。attention バッチは④へ (二重計上バグ修正 2026-07-08)
//   ③ 今日のルーチン     — GET /api/dayplan 導出ビュー。済=緑チェック/未=グレー/遅延=赤。
//                          skip は理由必須のインライン入力 (window.confirm/prompt 禁止)。snooze は1クリック。
//   ④ 対応待ち(要修正)   — dead_letter ジョブ (インライン再試行) + attention バッチ (失敗/不一致/停滞)
// + ヘルスstrip (runner/queue/sweep 1行) + KPI 数値カード4枚 (対応待ち(承認)/対応待ち(要修正)/ルーチン残/dead_letter)。
// 件数0のセクションは畳む。60秒自動更新 + 手動更新。dayplan API 未デプロイ時は③のみエラー表示。
// デザインは BatchesPage のパレットC・カード様式準拠。

import React, { useCallback, useEffect, useState } from 'react';
import { Container, Button, InlineSpinner, MessageBlock } from '@freee_jp/vibes';
import {
  MdRefresh, MdCheckCircle, MdWarningAmber, MdRadioButtonUnchecked,
  MdRemoveCircleOutline, MdHelpOutline, MdChevronRight, MdWbSunny,
} from 'react-icons/md';
import {
  fetchBatches, fetchJobs, fetchHealthPipeline, approveJob, retryJob,
  fetchDayplan, overrideDayplan, fetchFindings,
  BatchSummary, BatchCounts, Job, PipelineHealth, DayplanResponse, DayplanItem, Finding,
} from '../api/client';
import { FINDING_KIND_LABEL, FINDING_SEVERITY_META, FINDING_LAYER_OF_KIND } from './FindingsPage';
import { batchStatusToUnified, jobStatusToUnified } from '../lib/statusVocab';

const C = {
  ground: '#edf1f5', ink: '#1d2a3a', sub: '#647386', line: '#d9e0e7', lineDark: '#bdc8d3',
  navy: '#203a57', good: '#267a62', goodBg: '#edf8f4', warn: '#a46a12', warnBg: '#fdf3e3',
  danger: '#b3261e', dangerBg: '#fdecea',
};
const NUM = '"SFMono-Regular",Consolas,Menlo,monospace';
const FONT = '"Hiragino Sans","Yu Gothic UI","Meiryo",sans-serif';

const S = {
  page: { background: C.ground, minHeight: '100vh', paddingBottom: '3rem', color: C.ink, fontFamily: FONT, fontSize: 14 } as React.CSSProperties,
  sheet: { border: '1px solid #c8d1db', borderRadius: 10, background: '#fff', boxShadow: '0 9px 22px rgba(33,52,74,.08)', marginTop: 16, overflow: 'hidden' } as React.CSSProperties,
  top: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, minHeight: 58, padding: '8px 18px 8px 20px', color: '#fff', background: C.navy, flexWrap: 'wrap' } as React.CSSProperties,
  topTitle: { margin: 0, fontSize: 19, letterSpacing: '.025em', fontWeight: 700 } as React.CSSProperties,
  topCaption: { color: '#c9d6e5', fontSize: 12, fontWeight: 600 } as React.CSSProperties,
  pill: { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', border: '1px solid rgba(255,255,255,.35)', borderRadius: 13, background: 'rgba(255,255,255,.12)', color: '#e5eef8', fontSize: 11, fontWeight: 800, whiteSpace: 'nowrap' } as React.CSSProperties,
  healthStrip: { display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', padding: '6px 20px', background: '#16293e', color: '#aebfd3', fontSize: 11, fontWeight: 700, fontFamily: NUM } as React.CSSProperties,
  kpiRow: { display: 'flex', gap: 10, padding: '14px 18px 0', flexWrap: 'wrap', background: '#f7f9fb' } as React.CSSProperties,
  kpi: (accent: string): React.CSSProperties => ({ flex: '1 1 150px', minWidth: 140, border: `1px solid ${C.lineDark}`, borderTop: `3px solid ${accent}`, borderRadius: 7, background: '#fff', padding: '10px 14px' }),
  kpiVal: (color: string): React.CSSProperties => ({ fontFamily: NUM, fontSize: 26, fontWeight: 900, color, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }),
  kpiLabel: { fontSize: 11, color: C.sub, fontWeight: 800, letterSpacing: '.04em' } as React.CSSProperties,
  body: { padding: '14px 18px 18px', background: '#f7f9fb' } as React.CSSProperties,
  section: { marginBottom: 14, border: `1px solid ${C.lineDark}`, borderRadius: 7, background: '#fff', overflow: 'hidden' } as React.CSSProperties,
  sectionHead: (accent: string): React.CSSProperties => ({ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px', borderBottom: `1px solid ${C.line}`, borderLeft: `4px solid ${accent}`, background: '#fbfcfd', fontWeight: 800, fontSize: 13 }),
  sectionCollapsed: { display: 'flex', alignItems: 'center', gap: 8, padding: '7px 14px', marginBottom: 10, border: `1px dashed ${C.lineDark}`, borderRadius: 7, background: '#f1f4f7', color: C.sub, fontSize: 12, fontWeight: 700 } as React.CSSProperties,
  countBadge: { fontFamily: NUM, fontSize: 12, fontWeight: 900, padding: '1px 8px', borderRadius: 10, background: '#eef2f6', border: `1px solid ${C.lineDark}`, color: C.ink } as React.CSSProperties,
  row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 14px', borderBottom: `1px solid ${C.line}`, flexWrap: 'wrap' } as React.CSSProperties,
  rowClickable: { cursor: 'pointer' } as React.CSSProperties,
  counter: (color: string): React.CSSProperties => ({ fontFamily: NUM, fontSize: 15, fontWeight: 900, color, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }),
  badge: (bg: string, fg: string, bd: string): React.CSSProperties => ({ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 3, fontSize: 10, fontWeight: 800, background: bg, color: fg, border: `1px solid ${bd}`, whiteSpace: 'nowrap' }),
  sub: { color: C.sub, fontSize: 11 } as React.CSSProperties,
  reasonInput: { border: `1px solid ${C.lineDark}`, borderRadius: 4, padding: '5px 8px', fontSize: 12, fontFamily: FONT, minWidth: 220 } as React.CSSProperties,
};

const parseCounts = (b: BatchSummary): BatchCounts => {
  try { return { pending: 0, registering: 0, posted: 0, verified: 0, verify_failed: 0, failed: 0, unverifiable: 0, ...(JSON.parse(b.counts_json || '{}')) }; }
  catch { return { pending: 0, registering: 0, posted: 0, verified: 0, verify_failed: 0, failed: 0, unverifiable: 0 }; }
};

const fmtTs = (t: number | null | undefined) => {
  if (!t) return '—';
  const d = new Date(t); const p = (x: number) => String(x).padStart(2, '0');
  return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

const todayLabel = () => {
  const d = new Date();
  const wd = ['日', '月', '火', '水', '木', '金', '土'][d.getDay()];
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日(${wd})`;
};

// ルーチン item → 解決画面 hash の対応 (key/name のトリガ語で決定論マッチ)
const ROUTINE_HASH: Array<[RegExp, string]> = [
  [/nippou|日報|スマレジ/i, '#nippou'],
  [/payroll|給与|kot/i, '#payroll'],
  [/month_end|月末/i, '#month_end'],
  [/alsok/i, '#alsok'],
  [/seibu|西武/i, '#seibu_ledger'],
  [/filebox|ファイルボックス|レシート/i, '#filebox'],
  [/fixed|定額/i, '#fixed'],
  [/market_rent|市場家賃/i, '#market_rent'],
  [/bank|銀行|振替|引落/i, '#bank'],
  [/invoice|25日|請求書/i, '#invoice'],
];
const routineHash = (it: DayplanItem): string | null => {
  const hay = `${it.key} ${it.name}`;
  for (const [re, h] of ROUTINE_HASH) if (re.test(hay)) return h;
  return null;
};

const jobPayloadBrief = (j: Job): string => {
  try {
    const p = JSON.parse(j.payload_json || 'null');
    if (!p) return '';
    const s = Object.entries(p).slice(0, 3).map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : String(v)}`).join(' ');
    return s.length > 90 ? `${s.slice(0, 90)}…` : s;
  } catch { return ''; }
};

const jobErrorBrief = (j: Job): string => {
  try {
    const e = JSON.parse(j.error_json || 'null');
    if (!e) return '';
    return `${e.message_user || e.message || e.code || ''}${e.next_action ? ` → ${e.next_action}` : ''}`;
  } catch { return ''; }
};

const DP_ICON: Record<string, { Icon: React.ComponentType<any>; color: string; label: string }> = {
  done: { Icon: MdCheckCircle, color: C.good, label: '完了' },
  pending: { Icon: MdRadioButtonUnchecked, color: C.sub, label: '未' },
  overdue: { Icon: MdWarningAmber, color: C.danger, label: '遅延' },
  skipped: { Icon: MdRemoveCircleOutline, color: C.sub, label: 'skip済' },
  unknown: { Icon: MdHelpOutline, color: C.sub, label: '不明' },
};


// dayplan detail はAPI述語により文字列/オブジェクト両方あり得る — オブジェクトをReact childに渡すと
// React error #31 (白画面) になるため必ず文字列化する (2026-07-05 白画面バグ修正)
const fmtDetail = (d: unknown): string => {
  if (d == null) return '';
  if (typeof d === 'string') return d;
  if (typeof d === 'number' || typeof d === 'boolean') return String(d);
  try {
    return Object.entries(d as Record<string, unknown>)
      .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
      .join(' ／ ').slice(0, 160);
  } catch { return ''; }
};

export const InboxPage: React.FC = () => {
  const [batches, setBatches] = useState<BatchSummary[]>([]);
  const [awaitingJobs, setAwaitingJobs] = useState<Job[]>([]);
  const [deadJobs, setDeadJobs] = useState<Job[]>([]);
  const [health, setHealth] = useState<PipelineHealth | null>(null);
  const [dayplan, setDayplan] = useState<DayplanResponse | null>(null);
  const [dayplanErr, setDayplanErr] = useState<string | null>(null);
  const [findings, setFindings] = useState<Finding[]>([]);
  const [findingsErr, setFindingsErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [skipKey, setSkipKey] = useState<string | null>(null);
  const [skipReason, setSkipReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const [rb, rj, rd, rh, rp, rf] = await Promise.allSettled([
      fetchBatches(),
      fetchJobs({ status: 'awaiting_approval', limit: 100 }),
      fetchJobs({ status: 'dead_letter', limit: 100 }),
      fetchHealthPipeline(),
      fetchDayplan(),
      fetchFindings({ status: 'open' }),
    ]);
    const errs: string[] = [];
    if (rb.status === 'fulfilled') setBatches(rb.value.batches || []); else errs.push(`バッチ: ${rb.reason?.message || rb.reason}`);
    if (rj.status === 'fulfilled') setAwaitingJobs(rj.value.jobs || []); else errs.push(`承認待ちジョブ: ${rj.reason?.message || rj.reason}`);
    if (rd.status === 'fulfilled') setDeadJobs(rd.value.jobs || []); else errs.push(`dead_letter: ${rd.reason?.message || rd.reason}`);
    if (rh.status === 'fulfilled') setHealth(rh.value); else setHealth(null);
    // dayplan は API 未デプロイでも他セクションを止めない — ③にのみエラー表示 (裁定11)
    if (rp.status === 'fulfilled') { setDayplan(rp.value); setDayplanErr(null); }
    else { setDayplan(null); setDayplanErr(String(rp.reason?.message || rp.reason)); }
    // findings も API 未デプロイで他セクションを止めない — ⑤にのみ注記表示 (Phase 4)
    if (rf.status === 'fulfilled') { setFindings(rf.value.findings || []); setFindingsErr(null); }
    else { setFindings([]); setFindingsErr(String(rf.reason?.message || rf.reason)); }
    setMsg(errs.length ? `取得失敗 — ${errs.join(' / ')}` : null);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  // 60秒自動更新 (受信箱=常時開いておく前提のホーム画面)
  useEffect(() => {
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const doApprove = useCallback(async (id: string) => {
    setBusyId(id);
    try { await approveJob(id); await load(); }
    catch (e: unknown) { setMsg(`承認失敗: ${e instanceof Error ? e.message : String(e)}`); }
    finally { setBusyId(null); }
  }, [load]);

  const doRetry = useCallback(async (id: string) => {
    setBusyId(id);
    try { await retryJob(id); await load(); }
    catch (e: unknown) { setMsg(`再試行失敗: ${e instanceof Error ? e.message : String(e)}`); }
    finally { setBusyId(null); }
  }, [load]);

  const doOverride = useCallback(async (item: DayplanItem, action: 'skip' | 'snooze', reason: string) => {
    if (!dayplan) return;
    setBusyId(`dp:${item.key}`);
    try {
      await overrideDayplan({ date: dayplan.date, item_key: item.key, action, reason, by: 'web_app_user' });
      setSkipKey(null); setSkipReason('');
      await load();
    } catch (e: unknown) { setMsg(`${action}失敗: ${e instanceof Error ? e.message : String(e)}`); }
    finally { setBusyId(null); }
  }, [dayplan, load]);

  // -- 区分 (裁定11の4セクション) --
  //   attention バッチは④「対応待ち(要修正)」にのみ計上する (旧: ②と④に二重計上する構造バグ — 2026-07-08修正)
  const openBatches = batches.filter((b) => b.status === 'open');
  const attentionBatches = batches.filter((b) => b.status === 'attention');
  const dpItems = dayplan?.items || [];
  const routineRemain = dpItems.filter((it) => it.status === 'pending' || it.status === 'overdue').length;

  const CounterRow: React.FC<{ b: BatchSummary }> = ({ b }) => {
    const c = parseCounts(b);
    const registered = c.posted + c.verified + c.verify_failed;
    const regDone = registered >= b.total_count && c.failed === 0;
    const allGreen = c.verified === b.total_count && c.unverifiable === 0;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={S.counter(regDone ? C.good : c.failed > 0 ? C.danger : C.ink)}>登録 {Math.min(registered, b.total_count)}/{b.total_count}</span>
        <span style={S.counter(allGreen ? C.good : c.verify_failed > 0 ? C.danger : C.warn)}>照合 {c.verified}/{b.total_count}</span>
        {allGreen && b.total_count > 0 && <span style={S.badge(C.goodBg, C.good, '#9fdbc7')}><MdCheckCircle size={12} /> 全件照合済み</span>}
        {(c.failed > 0 || c.verify_failed > 0) && <span style={S.badge(C.dangerBg, C.danger, '#e6b0ac')}><MdWarningAmber size={12} /> 失敗{c.failed}・不一致{c.verify_failed}</span>}
        <MdChevronRight color={C.sub} />
      </div>
    );
  };

  const BatchRow: React.FC<{ b: BatchSummary }> = ({ b }) => (
    <div
      style={{ ...S.row, ...S.rowClickable }}
      onClick={() => { window.location.hash = `#batches/${b.id}`; }}
      title="クリックでバッチ台帳の詳細へ"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <b style={{ fontFamily: NUM, fontSize: 12 }}>{b.id}</b>
          <span style={S.badge('#f3f8fd', '#476b8e', '#d7e2ef')}>{b.kind}</span>
          {b.status === 'attention' && (() => { const u = batchStatusToUnified('attention'); return <span title="batches.status=attention" style={S.badge(u.bg, u.fg, u.bd)}>{u.label}</span>; })()}
        </div>
        <span style={S.sub}>{fmtTs(b.created_at)} ／ {b.total_count}件{b.total_amount ? ` ¥${Number(b.total_amount).toLocaleString('ja-JP')}` : ''}</span>
      </div>
      <CounterRow b={b} />
    </div>
  );

  const Section: React.FC<{ no: string; title: string; accent: string; count: number; caption?: string; forceOpen?: boolean; children?: React.ReactNode }> =
    ({ no, title, accent, count, caption, forceOpen, children }) => {
      if (count === 0 && !forceOpen) {
        return (
          <div style={S.sectionCollapsed}>
            <span>{no} {title}</span>
            <span style={{ ...S.countBadge, background: '#e8ecf0' }}>0件</span>
            <span style={{ fontWeight: 500 }}>— なし</span>
          </div>
        );
      }
      return (
        <section style={S.section}>
          <header style={S.sectionHead(accent)}>
            <span>{no} {title}</span>
            <span style={S.countBadge}>{count}件</span>
            {caption && <span style={{ ...S.sub, fontWeight: 600 }}>{caption}</span>}
          </header>
          <div>{children}</div>
        </section>
      );
    };

  const runnerRaw: any = (health as any)?.runner;
  const runnerText = runnerRaw == null ? '—'
    : typeof runnerRaw === 'string' ? runnerRaw
    : runnerRaw.status || (runnerRaw.active ? 'active' : 'idle');

  return (
    <div style={S.page}>
      <Container>
        <section style={S.sheet}>
          <header style={S.top}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <h1 style={S.topTitle}><MdWbSunny size={18} style={{ verticalAlign: '-2px', marginRight: 6 }} />受信箱 — 今日やること</h1>
              <span style={S.topCaption}>{todayLabel()}</span>
              <span style={S.pill}>ルーチン完了 = データからの決定論導出 (チェックボックスではない)</span>
            </div>
            <Button appearance="secondary" small onClick={load} disabled={loading}>
              {loading ? <InlineSpinner isLoading /> : <MdRefresh />} 更新
            </Button>
          </header>

          {/* ヘルスstrip: runner / queue / sweep を1行 */}
          <div style={S.healthStrip}>
            <span>runner: {runnerText}</span>
            <span>queue: 指示待ち {health ? health.instructions.pending : '—'}</span>
            <span>batches: open {health ? health.batches.open : '—'} / attention {health ? health.batches.attention : '—'}</span>
            <span style={health?.sweep.stale ? { color: '#ff9d94', fontWeight: 900 } : undefined}>
              sweep: {health ? fmtTs(health.sweep.last_run_at) : '—'}{health?.sweep.stale ? ' (STALE)' : ''}
            </span>
            <span style={{ marginLeft: 'auto', opacity: .7 }}>60秒自動更新</span>
          </div>

          {msg && <div style={{ padding: 12 }}><MessageBlock error>{msg}</MessageBlock></div>}

          {/* KPI 数値カード4枚 */}
          <div style={S.kpiRow}>
            <div style={S.kpi(awaitingJobs.length > 0 ? C.warn : C.lineDark)}>
              <div style={S.kpiVal(awaitingJobs.length > 0 ? C.warn : C.sub)}>{awaitingJobs.length}</div>
              <div style={S.kpiLabel}>対応待ち(承認) ジョブ — ②で承認</div>
            </div>
            <div style={S.kpi(attentionBatches.length > 0 ? C.warn : C.lineDark)}>
              <div style={S.kpiVal(attentionBatches.length > 0 ? C.warn : C.sub)}>{attentionBatches.length}</div>
              <div style={S.kpiLabel}>対応待ち(要修正) バッチ — ④で対応</div>
            </div>
            <div style={S.kpi(routineRemain > 0 ? C.navy : C.lineDark)}>
              <div style={S.kpiVal(routineRemain > 0 ? C.ink : C.sub)}>{dayplanErr ? '—' : routineRemain}</div>
              <div style={S.kpiLabel}>ルーチン残</div>
            </div>
            <div style={S.kpi(deadJobs.length > 0 ? C.danger : C.lineDark)}>
              <div style={S.kpiVal(deadJobs.length > 0 ? C.danger : C.sub)}>{deadJobs.length}</div>
              <div style={S.kpiLabel}>dead_letter</div>
            </div>
          </div>

          <div style={S.body}>
            {/* ① 今日のバッチ */}
            <Section no="①" title="今日のバッチ" accent={C.navy} count={openBatches.length} caption="登録・照合が進行中の一括処理 (クリックで台帳詳細へ)">
              {openBatches.map((b) => <BatchRow key={b.id} b={b} />)}
            </Section>

            {/* ② 対応待ち(承認) — awaiting_approval ジョブのみ (attention バッチ=失敗対応は④へ) */}
            <Section no="②" title="対応待ち(承認)" accent={C.warn} count={awaitingJobs.length} caption="内容を確認して承認すると前進します">
              {awaitingJobs.map((j) => (
                <div key={j.id} style={S.row}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      {(() => { const u = jobStatusToUnified('awaiting_approval'); return <span title="job.status=awaiting_approval" style={S.badge(u.bg, u.fg, u.bd)}>{u.label}</span>; })()}
                      <b style={{ fontSize: 13 }}>{j.type}</b>
                      <span style={{ ...S.sub, fontFamily: NUM }}>{j.id}</span>
                    </div>
                    <span style={S.sub}>{fmtTs(j.created_at)} ／ {j.created_by || j.origin}{jobPayloadBrief(j) ? ` ／ ${jobPayloadBrief(j)}` : ''}</span>
                  </div>
                  <Button appearance="primary" small disabled={busyId === j.id} onClick={() => doApprove(j.id)}>
                    {busyId === j.id ? <InlineSpinner isLoading /> : '承認'}
                  </Button>
                </div>
              ))}
            </Section>

            {/* ③ 今日のルーチン */}
            <Section
              no="③" title="今日のルーチン" accent={C.good}
              count={dpItems.length}
              caption={dayplan ? `${dayplan.date} ／ 残り${routineRemain}件` : undefined}
              forceOpen={!!dayplanErr}
            >
              {dayplanErr && (
                <div style={{ padding: 12 }}>
                  <MessageBlock error>
                    今日のルーチンを取得できませんでした (/api/dayplan): {dayplanErr}
                    <div style={{ fontSize: 11, marginTop: 4 }}>他のセクションは通常どおり動作しています。</div>
                  </MessageBlock>
                </div>
              )}
              {dpItems.map((it) => {
                const meta = DP_ICON[it.status] || DP_ICON.unknown;
                const hash = routineHash(it);
                // skip/snooze は「今日due」の未完了のみ (dueでない将来予定に操作を出さない — 2026-07-07 UIデバッグ)
                const actionable = (it.status === 'pending' || it.status === 'overdue') && it.due === true;
                const skipOpen = skipKey === it.key;
                const busy = busyId === `dp:${it.key}`;
                return (
                  <div key={it.key} style={S.row}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flexWrap: 'wrap' }}>
                      <meta.Icon size={18} color={meta.color} />
                      {hash ? (
                        <a href={hash} style={{ fontWeight: 800, fontSize: 13, color: it.status === 'overdue' ? C.danger : C.ink, textDecoration: it.status === 'skipped' ? 'line-through' : 'underline' }}>
                          {it.name}
                        </a>
                      ) : (
                        <span style={{ fontWeight: 800, fontSize: 13, color: it.status === 'overdue' ? C.danger : C.ink, textDecoration: it.status === 'skipped' ? 'line-through' : undefined }}>
                          {it.name}
                        </span>
                      )}
                      <span style={S.badge(
                        it.status === 'done' ? C.goodBg : it.status === 'overdue' ? C.dangerBg : '#f1f4f7',
                        it.status === 'done' ? C.good : it.status === 'overdue' ? C.danger : C.sub,
                        it.status === 'done' ? '#9fdbc7' : it.status === 'overdue' ? '#e6b0ac' : '#ccd6df',
                      )}>{meta.label}</span>
                      {!it.due && it.scheduled_date && <span style={{ ...S.sub, fontFamily: NUM }}>予定 {it.scheduled_date}</span>}
                      {it.detail != null && <span style={S.sub}>{fmtDetail(it.detail)}</span>}
                    </div>
                    {actionable && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        {skipOpen ? (
                          <>
                            <input
                              style={S.reasonInput}
                              placeholder="skipする理由 (必須・監査記録に残ります)"
                              value={skipReason}
                              autoFocus
                              onChange={(e) => setSkipReason(e.target.value)}
                            />
                            <Button appearance="primary" small disabled={!skipReason.trim() || busy} onClick={() => doOverride(it, 'skip', skipReason.trim())}>
                              {busy ? <InlineSpinner isLoading /> : 'skip確定'}
                            </Button>
                            <Button appearance="secondary" small disabled={busy} onClick={() => { setSkipKey(null); setSkipReason(''); }}>取消</Button>
                          </>
                        ) : (
                          <>
                            <Button appearance="secondary" small disabled={busy} onClick={() => { setSkipKey(it.key); setSkipReason(''); }}>skip</Button>
                            <Button appearance="secondary" small disabled={busy} onClick={() => doOverride(it, 'snooze', '翌日へ延期')}>
                              {busy ? <InlineSpinner isLoading /> : 'snooze'}
                            </Button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </Section>

            {/* ④ 対応待ち(要修正) — dead_letter ジョブ + attention バッチ (旧「例外」。失敗の中身は赤の事実チップで表示) */}
            <Section no="④" title="対応待ち(要修正)" accent={C.warn} count={deadJobs.length + attentionBatches.length} caption="再試行・修正で前進します — 無言で消えたものはここに残る">
              {deadJobs.map((j) => (
                <div key={j.id} style={S.row}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={S.badge(C.dangerBg, C.danger, '#e6b0ac')}>dead_letter</span>
                      <b style={{ fontSize: 13 }}>{j.type}</b>
                      <span style={{ ...S.sub, fontFamily: NUM }}>{j.id}</span>
                      <span style={{ ...S.sub, fontFamily: NUM }}>attempt {j.attempt}/{j.max_attempts}</span>
                    </div>
                    <span style={{ ...S.sub, color: jobErrorBrief(j) ? C.danger : C.sub }}>
                      {fmtTs(j.updated_at)}{jobErrorBrief(j) ? ` ／ ${jobErrorBrief(j)}` : ''}
                    </span>
                  </div>
                  <Button appearance="primary" small disabled={busyId === j.id} onClick={() => doRetry(j.id)}>
                    {busyId === j.id ? <InlineSpinner isLoading /> : '再試行'}
                  </Button>
                </div>
              ))}
              {attentionBatches.map((b) => <BatchRow key={b.id} b={b} />)}
            </Section>

            {/* 毎朝ダイジェスト 1 行 (2026-10-21): open findings を ①取込 / ②提案 / ③登録 の 3 層で集計。
                問題なしでも常に出す (静か = 確認済み、ではなく「0 件を確認した」が見える)。 */}
            {!findingsErr && (() => {
              const layers = { intake: 0, proposal: 0, register: 0 } as Record<'intake' | 'proposal' | 'register', number>;
              let other = 0;
              for (const f of findings) {
                const l = FINDING_LAYER_OF_KIND[f.kind];
                if (l) layers[l]++; else other++;
              }
              const cell = (label: string, n: number) => (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ fontWeight: 800 }}>{label}</span>
                  <span style={{ ...S.badge(n ? C.dangerBg : C.goodBg, n ? C.danger : C.good, n ? '#e6b0ac' : '#bfe3d3'), fontFamily: NUM }}>{n ? `${n}件` : '○'}</span>
                </span>
              );
              return (
                <div style={{ ...S.sectionCollapsed, borderLeft: `4px solid ${layers.intake + layers.proposal + layers.register ? C.danger : C.good}` }}>
                  <span>今朝の点検</span>
                  {cell('①取込', layers.intake)}
                  {cell('②提案', layers.proposal)}
                  {cell('③登録', layers.register)}
                  {other > 0 && <span style={{ ...S.sub, fontWeight: 600 }}>他 {other}件</span>}
                  <span style={{ ...S.sub, fontWeight: 500 }}>— 請求書の取込 / 仕訳提案 / freee 登録の各層で open の監査指摘 (○ = 0 件)</span>
                </div>
              );
            })()}

            {/* 監査指摘 (Phase 4): open findings 上位5件 + 件数バッジ + #findings へのリンク */}
            {findingsErr ? (
              <div style={S.sectionCollapsed}>
                <span>⑤ 監査指摘</span>
                <span style={{ fontWeight: 500 }}>— 取得不可 (/api/findings): {findingsErr}</span>
                <a href="#findings" style={{ marginLeft: 'auto', color: '#476b8e', fontWeight: 700 }}>監査ページへ</a>
              </div>
            ) : (
              <Section no="⑤" title="監査指摘" accent={C.warn} count={findings.length} caption="対応待ち(監査確認) — #findings で確認済/却下の判定を (上位5件表示)">
                {findings.slice(0, 5).map((f) => {
                  const sev = FINDING_SEVERITY_META[f.severity] || FINDING_SEVERITY_META.low;
                  return (
                    <div
                      key={f.id}
                      style={{ ...S.row, ...S.rowClickable }}
                      onClick={() => { window.location.hash = '#findings'; }}
                      title="クリックで監査ページへ"
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0 }}>
                        <span style={S.badge(sev.bg, sev.fg, sev.bd)}>{sev.label}</span>
                        <span style={S.badge('#f3f8fd', '#476b8e', '#d7e2ef')}>{FINDING_KIND_LABEL[f.kind] || f.kind}</span>
                        <b style={{ fontSize: 12 }}>{f.summary}</b>
                      </div>
                      <span style={{ ...S.sub, fontFamily: NUM }}>{fmtTs(f.created_at)}</span>
                    </div>
                  );
                })}
                <div style={{ padding: '8px 14px' }}>
                  <a href="#findings" style={{ color: '#476b8e', fontSize: 12, fontWeight: 800 }}>
                    すべての監査指摘を見る ({findings.length}件) <MdChevronRight size={13} style={{ verticalAlign: '-2px' }} />
                  </a>
                </div>
              </Section>
            )}
          </div>
        </section>
      </Container>
    </div>
  );
};

export default InboxPage;
